import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MINT_START_DATE } from "@/config/mint";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

const Digit = ({ value }: { value: string }) => (
  <div className="relative w-8 h-8 overflow-hidden inline-block">
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={value}
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        exit={{ y: -20 }}
        transition={{
          duration: 0.2,
          ease: "easeInOut",
          delay: 0
        }}
        className="font-mono text-3xl font-bold text-[#9333EA] absolute inset-0 flex items-center justify-center"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </div>
);

function updateMetaTags() {
  // Update Open Graph image
  const ogImage = document.querySelector('meta[property="og:image"]');
  if (ogImage) {
    ogImage.setAttribute('content', '/brand-images/SBF.png');
  }

  // Update Twitter image
  const twitterImage = document.querySelector('meta[property="twitter:image"]');
  if (twitterImage) {
    twitterImage.setAttribute('content', '/brand-images/SBF.png');
  }

  // Update descriptions
  const ogDesc = document.querySelector('meta[property="og:description"]');
  const twitterDesc = document.querySelector('meta[property="twitter:description"]');
  const newDesc = "🎭 The wait is over! Mint your piece of crypto history now! Featuring SBF and other notorious figures from the crypto world. Limited supply - don't miss out! 🌟";
  
  if (ogDesc) ogDesc.setAttribute('content', newDesc);
  if (twitterDesc) twitterDesc.setAttribute('content', newDesc);
}

export function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());
  const [hasEnded, setHasEnded] = useState(false);

  function calculateTimeLeft(): TimeLeft {
    const difference = +new Date(MINT_START_DATE) - +new Date();
    
    if (difference <= 0) {
      return {
        total: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }

    return {
      total: difference,
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }

  useEffect(() => {
    // Check if we're past the mint date on component mount
    const currentTime = calculateTimeLeft();
    if (currentTime.total <= 0 && !hasEnded) {
      setHasEnded(true);
      updateMetaTags();
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      if (newTimeLeft.total <= 0 && !hasEnded) {
        setHasEnded(true);
        updateMetaTags();
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [hasEnded]);

  const timeUnits = [
    { label: 'DAYS', value: timeLeft.days },
    { label: 'HOURS', value: timeLeft.hours },
    { label: 'MINUTES', value: timeLeft.minutes },
    { label: 'SECONDS', value: timeLeft.seconds }
  ];

  return (
    <div className="flex flex-col items-center justify-center space-y-2">
      <div className="grid grid-cols-4 gap-4 text-center">
        {timeUnits.map(({ label, value }) => (
          <div key={label} className="bg-black/60 backdrop-blur-sm rounded-lg p-4">
            <div className="text-3xl font-bold font-mono text-[#9333EA]">
              {String(value).padStart(2, '0')}
            </div>
            <div className="text-xs uppercase tracking-wider text-nft-muted mt-1">
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 